import { useState } from "react";

import { makeRequest } from "../../utils/request";
import { Badge, Dialog, InputField, Text, View } from "../../components/base";
import { ActivityBarChart, ADMIN_CHART_COLORS, Section, useAdminFetch } from "../../components/admin";
import { getColorForName } from "../../utils/colors";
import { formatCount, formatDateShort, formatDollars, formatRelative } from "../../utils/adminFormat";
import type { OrgActivityDetail } from "../../types/admin";

/** Shared pieces between the organizations list (shelf view) and the
 *  dedicated /admin/organizations/:organizationId page. The shelf shows the
 *  same activity detail read-only; org management (edit/delete) lives only
 *  on the dedicated page. */

export const TIER_OPTIONS = [
  { label: "Community", value: "community" },
  { label: "Enterprise", value: "enterprise" },
];

/** The org's pricing strategy (OrganizationBilling.billingModel). Each
 *  strategy owns its rate fields — the API 400s on fields from the other
 *  strategy, so the form only shows (and only submits) the active set. */
export const BILLING_MODEL_OPTIONS = [
  { label: "Flat (per-minute session rate)", value: "flat" },
  { label: "Unbundled (per-component rates)", value: "unbundled" },
];

export const FLAT_PRICING_KEYS = ["sessionCostPerMinute"] as const;

export const UNBUNDLED_PRICING_KEYS = [
  "audioRuntimeCostPerMinute",
  "agentRuntimeCostPerMinute",
  "mediaStreamsCostPerMinute",
  "telephonyOutboundCostPerMinute",
  "telephonyInboundCostPerMinute",
  "telephonySipOutboundCostPerMinute",
  "telephonySipInboundCostPerMinute",
  "sttUpchargeMultiplier",
  "ttsUpchargeMultiplier",
] as const;

/** Billed in both strategies. */
export const AGNOSTIC_PRICING_KEYS = ["evalsTokenCostMultiplier", "completionsTokenCostMultiplier", "phoneNumberMonthlyRate"] as const;

export type PricingRateKey = (typeof FLAT_PRICING_KEYS)[number] | (typeof UNBUNDLED_PRICING_KEYS)[number] | (typeof AGNOSTIC_PRICING_KEYS)[number];

/** Platform default rates — mirrors the OrganizationBilling column
 *  defaults. Used to prefill empty strategy fields when the admin switches
 *  pricing strategy, so a switch always submits explicit rates. */
export const PRICING_DEFAULTS: Record<PricingRateKey, string> = {
  sessionCostPerMinute: "0.08",
  audioRuntimeCostPerMinute: "0.015",
  agentRuntimeCostPerMinute: "0.015",
  mediaStreamsCostPerMinute: "0.004",
  telephonyOutboundCostPerMinute: "0.014",
  telephonyInboundCostPerMinute: "0.0085",
  telephonySipOutboundCostPerMinute: "0.01",
  telephonySipInboundCostPerMinute: "0.0034",
  sttUpchargeMultiplier: "1.0",
  ttsUpchargeMultiplier: "1.0",
  evalsTokenCostMultiplier: "1.2",
  completionsTokenCostMultiplier: "1.0",
  phoneNumberMonthlyRate: "1.15",
};

const ALL_PRICING_RATE_KEYS: readonly PricingRateKey[] = [...FLAT_PRICING_KEYS, ...UNBUNDLED_PRICING_KEYS, ...AGNOSTIC_PRICING_KEYS];

export type OrganizationSettings = {
  id: string;
  name: string;
  tier: string;
  outboundEnabled: boolean;
  billingModel?: "flat" | "unbundled";
  /** VoiceScore price ($/audio-minute) — deliberately OUTSIDE the strategy
   *  form: it saves independently via its own Pricing-tab section. */
  voicescoreCostPerMinute?: number | null;
  /** STT router live-stream cap override; null = platform default (5). Also
   *  outside the strategy form — capacity, not money. */
  sttMaxConcurrentStreams?: number | null;
} & Partial<Record<PricingRateKey, number>>;

export type EditFormState = {
  tier: string;
  outboundEnabled: boolean;
  billingModel: string;
} & Record<PricingRateKey, string>;

export const EMPTY_EDIT_FORM: EditFormState = {
  tier: "community",
  outboundEnabled: false,
  billingModel: "flat",
  ...(Object.fromEntries(ALL_PRICING_RATE_KEYS.map((key) => [key, ""])) as Record<PricingRateKey, string>),
};

export const settingsToEditForm = (data: OrganizationSettings): EditFormState => ({
  tier: data.tier,
  outboundEnabled: data.outboundEnabled,
  billingModel: data.billingModel ?? "flat",
  ...(Object.fromEntries(ALL_PRICING_RATE_KEYS.map((key) => [key, data[key] != null ? String(data[key]) : ""])) as Record<PricingRateKey, string>),
});

const inviteStatusVariant = (status: string) => {
  if (status === "accepted") return "success";
  if (status === "pending") return "warning";
  return "default";
};

export const OrgAvatar = ({ name }: { name: string }) => (
  <View className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${getColorForName(name || "?")}`}>
    {(name || "?").charAt(0).toUpperCase()}
  </View>
);

export const AddFundsDialog = ({
  isOpen,
  organizationId,
  organizationName,
  onClose,
}: {
  isOpen: boolean;
  organizationId: string | null;
  organizationName: string;
  onClose: () => void;
}) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setAmount("");
    setDescription("");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    const dollars = parseFloat(amount);
    if (isNaN(dollars) || dollars <= 0) {
      setError("Please enter a valid dollar amount.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await makeRequest("/v1/credits/adjust", {
        method: "POST",
        body: {
          organizationId,
          type: "credit",
          amount: Math.round(dollars * 1_000_000),
          description: description || undefined,
        },
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add funds.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add Funds — ${organizationName}`}
      size="sm"
      onSave={handleSave}
      saveLabel="Add Funds"
      isSaving={isSaving}
    >
      <View className="p-4 flex flex-col gap-4">
        <InputField
          label="Amount ($)"
          placeholder="10.00"
          type="number"
          value={amount}
          onChange={setAmount}
        />
        <InputField
          label="Description (optional)"
          placeholder="e.g. Courtesy credit"
          value={description}
          onChange={setDescription}
        />
        {error && <Text className="text-sm text-red-500">{error}</Text>}
      </View>
    </Dialog>
  );
};

/** Activity stat tiles + sessions chart + members / service accounts /
 *  invites / agents. Rendered identically in the shelf (2 tile columns) and
 *  on the dedicated org page (4 tile columns). */
export const OrgDetailSections = ({
  detail,
  periodLabel,
  tileColumns = 2,
  afterSessions,
}: {
  detail: OrgActivityDetail;
  periodLabel: string;
  tileColumns?: 2 | 4;
  /** Slot rendered between the Sessions chart and the Members list —
   *  used for the VoiceScore cost section. */
  afterSessions?: React.ReactNode;
}) => {
  const metrics = detail.metrics;
  const statTiles = [
    { label: "Sessions", value: formatCount(metrics.sessions) },
    { label: "Releases", value: formatCount(metrics.releases) },
    { label: "Pushes", value: formatCount(metrics.pushes) },
    { label: "Agents", value: `${formatCount(metrics.agentsTotal)}${metrics.agentsCreated > 0 ? ` (+${metrics.agentsCreated})` : ""}` },
    { label: "Phone numbers", value: formatCount(metrics.phoneNumbers) },
    { label: "Bug tickets", value: formatCount(metrics.bugTickets) },
    { label: "Dollars uploaded", value: formatDollars(metrics.creditsUploaded) },
    { label: "Dollars spent", value: formatDollars(metrics.creditsSpent) },
  ];

  return (
    <>
      <Section title={`Activity — ${periodLabel}`}>
        <View className={`grid gap-2 ${tileColumns === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
          {statTiles.map((tile) => (
            <View
              key={tile.label}
              className="rounded-lg border border-gray-200 dark:border-gray-800 p-3"
            >
              <Text className="block text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 truncate">{tile.label}</Text>
              <Text className="block text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{tile.value}</Text>
            </View>
          ))}
        </View>
      </Section>

      <Section title="Sessions">
        <ActivityBarChart
          data={detail.sessionsSeries}
          color={ADMIN_CHART_COLORS.sessions}
          height={140}
          valueLabel="sessions"
        />
      </Section>

      {afterSessions}

      <Section title="Members">
        {detail.members.length === 0 ? (
          <Text className="text-sm text-gray-400 dark:text-gray-500">No members</Text>
        ) : (
          <View className="flex flex-col">
            {detail.members.map((member) => {
              const parts = [
                member.agentsCreated > 0 && `${member.agentsCreated} agents`,
                member.releases > 0 && `${member.releases} releases`,
                member.bugTickets > 0 && `${member.bugTickets} bugs`,
                member.deploys > 0 && `${member.deploys} deploys`,
              ].filter(Boolean);
              return (
                <View
                  key={member.userId}
                  className="flex flex-row items-center gap-2.5 py-1.5"
                >
                  <OrgAvatar name={member.name || member.email} />
                  <View className="flex min-w-0 flex-1 flex-col">
                    <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{member.name || member.email}</Text>
                    {member.name && <Text className="truncate text-xs text-gray-400 dark:text-gray-500">{member.email}</Text>}
                  </View>
                  <Badge variant="default">{member.role}</Badge>
                  {parts.length > 0 && <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{parts.join(" · ")}</Text>}
                </View>
              );
            })}
          </View>
        )}
      </Section>

      {(detail.serviceAccounts?.length ?? 0) > 0 && (
        <Section title="Service Accounts">
          <View className="flex flex-col">
            {detail.serviceAccounts.map((account) => {
              const parts = [account.agentsCreated > 0 && `${account.agentsCreated} agents`, account.releases > 0 && `${account.releases} releases`].filter(
                Boolean,
              );
              return (
                <View
                  key={account.serviceAccountId}
                  className="flex flex-row items-center gap-2.5 py-1.5"
                >
                  <OrgAvatar name={account.name} />
                  <View className="flex min-w-0 flex-1 flex-col">
                    <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{account.name}</Text>
                  </View>
                  <Badge variant="default">service account</Badge>
                  {parts.length > 0 && <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{parts.join(" · ")}</Text>}
                </View>
              );
            })}
          </View>
        </Section>
      )}

      {detail.invites.length > 0 && (
        <Section title="Invites">
          <View className="flex flex-col">
            {detail.invites.map((invite) => (
              <View
                key={`${invite.email}-${invite.createdAt}`}
                className="flex flex-row items-center gap-2.5 py-1.5"
              >
                <View className="flex min-w-0 flex-1 flex-col">
                  <Text className="truncate text-sm text-gray-900 dark:text-gray-100">{invite.email}</Text>
                  <Text className="text-xs text-gray-400 dark:text-gray-500">
                    {invite.role} · {formatRelative(invite.createdAt)}
                  </Text>
                </View>
                <Badge variant={inviteStatusVariant(invite.status)}>{invite.status}</Badge>
              </View>
            ))}
          </View>
        </Section>
      )}

      <Section title="Agents">
        {detail.agents.length === 0 ? (
          <Text className="text-sm text-gray-400 dark:text-gray-500">No agents</Text>
        ) : (
          <View className="flex flex-col">
            {detail.agents.map((agent) => (
              <View
                key={agent.id}
                className="flex flex-row items-center gap-2.5 py-1.5"
              >
                <View className="flex min-w-0 flex-1 flex-col">
                  <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{agent.name || agent.id}</Text>
                  <Text className="text-xs text-gray-400 dark:text-gray-500">
                    Created {formatDateShort(agent.createdAt)} · last active {formatRelative(agent.lastActivityAt)}
                  </Text>
                </View>
                <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{formatCount(agent.sessions)} sessions</Text>
              </View>
            ))}
          </View>
        )}
      </Section>
    </>
  );
};

type VoiceScoreBillingData = {
  transcription: { calls: number; minutes: number; costUsd: number };
  scoring: { debits: number; costUsd: number };
  totalCostUsd: number;
  costPerMinuteUsd: number | null;
  /** The org's configured VoiceScore price (OrganizationBilling); null = not priced. */
  pricePerMinuteUsd: number | null;
  billableRevenueUsd: number | null;
  marginUsd: number | null;
  marginPercent: number | null;
};

// Sub-cent precision: VoiceScore per-minute costs are fractions of a cent.
const preciseDollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

/**
 * VoiceScore economics for one org: what VoiceScore COSTS VoiceRun over the
 * period, from the shadow meter (charged=false ledger rows, internal-only).
 * Measured facts only — pricing what-ifs live in the internal calculator
 * tooling, not here.
 */
export const VoiceScoreBillingSection = ({ organizationId, query, periodLabel }: { organizationId: string | null; query: string; periodLabel: string }) => {
  const { data, isLoading } = useAdminFetch<{ data: VoiceScoreBillingData }>(
    organizationId ? `/v1/admin/activity/organizations/${organizationId}/voicescore-billing?${query}` : null,
  );
  // The commercial tiles (price / revenue / margin) render MASKED by default
  // and reveal on click — so a screen-shared or screenshotted drawer shows
  // costs but never margin. Deliberately not persisted: every drawer open
  // starts hidden.
  const [commercialsRevealed, setCommercialsRevealed] = useState(false);

  const billing = data?.data ?? null;
  if (isLoading || !billing) return null;

  // Usage IS the "VoiceScore enabled" signal: orgs with no metered rows in
  // the window get no section at all — the drawer stays clean for the orgs
  // this doesn't concern, with no enabled-flag plumbing.
  const hasUsage = billing.transcription.calls > 0 || billing.scoring.debits > 0;
  if (!hasUsage) return null;

  const isPriced = billing.pricePerMinuteUsd !== null;
  const tiles: Array<{ label: string; value: string; valueClass?: string; masked?: boolean }> = [
    { label: "Calls transcribed", value: formatCount(billing.transcription.calls) },
    { label: "Minutes processed", value: formatCount(Math.round(billing.transcription.minutes)) },
    { label: "Transcription cost", value: preciseDollarFormatter.format(billing.transcription.costUsd) },
    { label: "LLM scoring cost", value: preciseDollarFormatter.format(billing.scoring.costUsd) },
    { label: "Total cost", value: preciseDollarFormatter.format(billing.totalCostUsd) },
    {
      label: "Cost per minute",
      value: billing.costPerMinuteUsd === null ? "—" : preciseDollarFormatter.format(billing.costPerMinuteUsd),
    },
    ...(isPriced
      ? [
          { label: "Price per minute", value: preciseDollarFormatter.format(billing.pricePerMinuteUsd ?? 0), masked: true },
          { label: "Billable revenue", value: preciseDollarFormatter.format(billing.billableRevenueUsd ?? 0), masked: true },
          {
            label: "Profit margin",
            value: `${preciseDollarFormatter.format(billing.marginUsd ?? 0)}${billing.marginPercent !== null ? ` (${billing.marginPercent.toFixed(1)}%)` : ""}`,
            valueClass: (billing.marginUsd ?? 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400",
            masked: true,
          },
        ]
      : []),
  ];

  return (
    <Section title={`VoiceScore cost to VoiceRun — ${periodLabel}`}>
      <View className="flex flex-col gap-2">
        <View className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {tiles.map((tile) => {
            const hidden = tile.masked && !commercialsRevealed;
            return (
              <View
                key={tile.label}
                title={tile.masked ? (hidden ? "Click to reveal — hidden by default for screen sharing" : "Click to hide") : undefined}
                onClick={tile.masked ? () => setCommercialsRevealed((r) => !r) : undefined}
                className={`rounded-lg border border-gray-200 dark:border-gray-800 p-3 ${tile.masked ? "cursor-pointer select-none" : ""}`}
              >
                <Text className="block text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 truncate">{tile.label}</Text>
                {/* While hidden, the value AND its color are suppressed — a green
                    dot row would still leak the margin's sign on a shared screen. */}
                <Text
                  className={`block text-lg font-semibold tabular-nums ${hidden ? "text-gray-400 dark:text-gray-500" : (tile.valueClass ?? "text-gray-900 dark:text-white")}`}
                >
                  {hidden ? "•••" : tile.value}
                </Text>
              </View>
            );
          })}
        </View>
        {!isPriced && (
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            No VoiceScore price configured — set it in the Pricing tab's VoiceScore section to see billable revenue and margin.
          </Text>
        )}
      </View>
    </Section>
  );
};
